import { AbstractControl, AbstractControlOptions, AsyncValidatorFn, FormArray, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { Observable } from 'rxjs';

/* eslint-disable prefer-arrow/prefer-arrow-functions, @typescript-eslint/unified-signatures, rxjs/finnish */

type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

abstract class TypedAbstractControlClass<T> extends AbstractControl {
  public value: T;
  public valueChanges: Observable<T>;
}

class TypedControlClass<T> extends FormControl {
  public value: T;
  public valueChanges: Observable<T>;
}

class TypedFormGroupClass<T> extends FormGroup {
  public value: T;
  public valueChanges: Observable<T>;
  public controls: {
    [K in keyof T]: TypedAbstractControlClass<T[K]>;
  };
}

interface InitialState<T> {
  value: T;
  disabled: boolean;
}

export type FormControls<T> = {
  [K in keyof Required<T>]: TypedFormControl<T[K]>;
};

export type PartialFormControls<T> = {
  [K in keyof Partial<T>]: TypedFormControl<T[K]>;
};

export type TypedAbstractControl<T> = TypedAbstractControlClass<T>;

export type TypedFormControl<T> = TypedControlClass<T>;

export type TypedFormGroup<T> = TypedFormGroupClass<T>;

export type TypedFormArray<T> = Omit<FormArray, 'value' | 'valueChanges' | 'controls' | 'push'> &
  TypedAbstractControl<T[]> & {
    controls: TypedAbstractControl<T>[];
    push(control: TypedAbstractControl<T>, options?: { emitEvent?: boolean }): void;
  };

export type SubFormGroup<T> = {
  [K in keyof Required<T>]: TypedFormGroup<T[K]>;
};

export type SubFormArray<T> = {
  [K in keyof Required<T>]: T[K] extends Array<unknown> ? TypedFormArray<ArrayElement<T[K]>> : never;
};

export function createFormControl<T>(
  initialState: InitialState<T>,
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): TypedFormControl<T>;
export function createFormControl<T>(
  initialValue: T,
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): TypedFormControl<T>;
export function createFormControl<T>(
  initialValueOrState: T | InitialState<T>,
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): TypedFormControl<T> {
  // @ts-ignore
  return new FormControl(initialValueOrState, validatorOrOpts, asyncValidator);
}

export function createForm<T>(
  controls: { [K in keyof T]: TypedAbstractControl<T[K]> },
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): TypedFormGroup<T> {
  return new FormGroup(controls, validatorOrOpts, asyncValidator) as TypedFormGroup<T>;
}

export function createFormArray<T>(
  formControls: TypedAbstractControl<T>[],
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): TypedFormArray<T> {
  // @ts-ignore
  return new FormArray(formControls, validatorOrOpts, asyncValidator);
}
